import type {
  ActionTaskInput,
  TaskOutputReferenceInput,
  WorkflowConfigInput,
} from '@automata/wfb-api';
import type {
  LabwareItemInput,
  ParameterReferenceInput,
  ReferenceDataInput,
  StaticDataInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';

import * as LabwareErrors from './labwareErrors';
import { ExtendedTaskOutputReferenceInput } from '../../types/taskNode';
import * as TaskErrors from '../workflowJsonSelectors/taskErrors';

export const INVALID_TIME_CONSTRAINT = 'INVALID_TIME_CONSTRAINT';

// TODO(IL, 2025-01-23): I think the 'errorType' may not be useful,
// because there are some errors that are just for a labware and have labwareId field,
// other errors for tasks that have taskId field,
// and other errors for a task<>labware interaction that have both.
// Consumers need to filter by those ID fields, eg "I want all the errors for labware A",
// so it's more important to have specifically named keys like 'labwareId'/'taskId'
// (and avoid ever just having 'id' fields, which are inconvenient/brittle to filter by).
//
// Assume that all errors with labwareId should be shown at the node for that labware,
// and all errors with taskId should be shown at the node for that task.
// In addition to the node, some of the errors might show up inside the labware/task
// properties panel.
export type LabwareSerializationError =
  | {
      errorType: 'labware';
      message:
        | typeof LabwareErrors.MISSING_LABWARE_DEFINITION_URI
        | typeof LabwareErrors.MISSING_START_LOCATION;
      labwareId: string;
    }
  | {
      errorType: 'labware';
      message: typeof LabwareErrors.INVALID_LABWARE_DEFINITION_URI;
      labwareId: string;
      labwareDefinitionURI: string;
    }
  | {
      errorType: 'labware';
      message:
        | typeof LabwareErrors.LABWARE_START_LOCATION_SLOT_COLLISION
        | typeof LabwareErrors.LABWARE_START_LOCATION_SELF_COLLISION
        | typeof LabwareErrors.LABWARE_START_LOCATION_SLOT_INVALID;
      labwareId: string;
      instrumentId?: string | null;
      slotsInUse?: number[];
    }
  | {
      errorType: 'labware';
      message: typeof LabwareErrors.LABWARE_START_LOCATION_SLOT_CAPACITY_EXCEEDED;
      labwareId: string;
      instrumentId?: string | null;
      capacity: number;
    }
  | {
      errorType: 'labware';
      message:
        | typeof LabwareErrors.LABWARE_FINAL_LOCATION_SLOT_COLLISION
        | typeof LabwareErrors.LABWARE_FINAL_LOCATION_SELF_COLLISION;
      labwareId: string;
      instrumentId?: string | null;
      slotsInUse?: number[];
      taskId: string;
    }
  | {
      errorType: 'labware';
      message: typeof LabwareErrors.LABWARE_FINAL_LOCATION_SLOT_CAPACITY_EXCEEDED;
      labwareId: string;
      instrumentId?: string | null;
      taskId: string;
      capacity: number;
    }
  | {
      errorType: 'labware';
      message: typeof LabwareErrors.LABWARE_SLOT_COLLISION;
      labwareId: string;
      instrumentId?: string | null;
      taskId: string;
    };

export type TaskSerializationError =
  | {
      errorType: 'tasks';
      message:
        | typeof TaskErrors.CIRCULAR_GRAPH
        | typeof TaskErrors.MINIMUM_GRAPH_REQUIREMENTS;
      taskId?: string;
    }
  | {
      errorType: 'tasks';
      message:
        | typeof TaskErrors.NO_LABWARE_FOR_TASK
        | typeof TaskErrors.NO_TASK_PROPERTIES
        | typeof TaskErrors.MISSING_INSTRUMENT
        | typeof TaskErrors.MISSING_TIME_ESTIMATE;
      taskId: string;
    }
  | {
      errorType: 'tasks';
      message: typeof TaskErrors.TASK_SLOT_FILLING_RULE_INVALID_SLOT_STRING;
      slot: string;
      taskId: string;
      instrumentId?: string | null;
    }
  | {
      errorType: 'tasks';
      message: typeof TaskErrors.INVALID_TASK_FIELD_VALUES;
      taskId: string;
    }
  | {
      errorType: 'tasks';
      message: 'LABWARE_SLOT_RANGE_GLOBAL_SIZE_MISMATCH';
      taskId?: string;
      labwareId: string;
    }
  | {
      errorType: 'tasks';
      message: 'CONSECUTIVE_TASK_SLOT_MISMATCH';
      taskId: string;
      labwareId: string;
    };

export type WorkflowSerializationError =
  | LabwareSerializationError
  | TaskSerializationError
  | {
      errorType: 'timeConstraints';
      message: typeof INVALID_TIME_CONSTRAINT;
    }
  | {
      errorType: 'taskDependencies';
      message: typeof TaskErrors.INVALID_TASK_DEPENDENCIES;
    };

// TODO(IL, 2024-04-24): maybe use a library and real TS Maybe type
export type MaybeWorkflowSerializationErrors<T> =
  | {
      isError: true;
      errors: WorkflowSerializationError[];
    }
  | {
      isError: false;
      data: T;
    };

export type MaybeActionTask = MaybeWorkflowSerializationErrors<
  ActionTaskInput & { type: 'action' }
>;

export type JsonLabwareById = {
  [labwareId: string]: LabwareItemInput;
};

export type MaybeWorkflowConfig =
  MaybeWorkflowSerializationErrors<WorkflowConfigInput>;

export type TimeEstimateValuesByTaskId = { [taskId: string]: number | null };

export type DataDependenciesByTaskId = Record<
  string,
  Record<string, ExtendedTaskOutputReferenceInput | ReferenceDataInput>
>;

export type InputValue =
  | StaticDataInput
  | TaskOutputReferenceInput
  | ReferenceDataInput
  | ParameterReferenceInput;
